import styled from 'styled-components';
import { colors, spacing } from 'ui/theme';
import DropZone from '../../DropZone';
import { TakeoverDetail } from './TakeoverDetail';
import { TakeoverList } from './TakeoverList';
import { UrlToFileInput } from '../../common';
import { TakeoverEmpty } from './TakeoverEmpty';
import { AssetListEmptyState } from '../../AssetListEmptyState';
import { useUploadAssetsContext } from '../../../contexts/UploadAssetsContext';
import { useTakeoverContext } from '../../../contexts/Takeover';
import { DropZoneArea, TakeoverIntent } from '../../../types';

const Container = styled.div`
  column-gap: ${spacing.base};
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 100%;
  height: 100%;
`;

const NoDropZone = styled.div`
  border: none;
  box-sizing: border-box;
  flex: 1 0 auto;
  position: relative;
`;

const InputContainer = styled.div`
  padding: ${spacing.base} ${spacing.double};
`;

const Panel = styled.div`
  border-radius: ${spacing.half};
  border: 1px solid ${colors.grey15};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-width: 100%;
  min-width: 0; /* Allows flex item to shrink below content size */
  
  /* Force all child content to respect panel width */
  & > * {
    max-width: 100%;
    min-width: 0;
    overflow-wrap: break-word;
    word-wrap: break-word;
  }
`;

export const testIds = {
  container: 'cl-index-takeover-body-container',
  noDropZone: 'cl-index-takeover-body-no-drop-zone',
  panelLeft: 'cl-index-takeover-body-panel-left',
  panelRight: 'cl-index-takeover-body-panel-right',
  urlInputField: 'cl-takeover-url-input-field',
};

export const TakeoverBodyContent = () => {
  return (
    <Container data-test={testIds.container}>
      <Panel data-test={testIds.panelLeft}>
        <TakeoverList />
      </Panel>
      <Panel data-test={testIds.panelRight}>
        <TakeoverDetail />
      </Panel>
    </Container>
  );
};

export const TakeoverBody = () => {
  const { activeDropZone, pendingImages } = useUploadAssetsContext();
  const { currentIntent, initialIntent } = useTakeoverContext();

  const hasImages = pendingImages.length > 0;
  const isEditMode = currentIntent === TakeoverIntent.EDIT;
  const isImportFromUrl =
    initialIntent === TakeoverIntent.IMPORT_FROM_URL ||
    currentIntent === TakeoverIntent.IMPORT_FROM_URL;
  const isDragging = activeDropZone === DropZoneArea.Takeover;

  if (isImportFromUrl && !hasImages) {
    return (
      <NoDropZone data-test={testIds.noDropZone}>
        <InputContainer>
          <UrlToFileInput inputTestId={testIds.urlInputField} />
        </InputContainer>
      </NoDropZone>
    );
  }

  if (isEditMode) {
    return (
      <NoDropZone data-test={testIds.noDropZone}>
        <TakeoverBodyContent />
      </NoDropZone>
    );
  }

  if (hasImages) {
    if (currentIntent === TakeoverIntent.IMPORT_FROM_URL) {
      return (
        <NoDropZone data-test={testIds.noDropZone}>
          <InputContainer>
            <UrlToFileInput inputTestId={testIds.urlInputField} />
          </InputContainer>
        </NoDropZone>
      );
    }
    return (
      <DropZone area={DropZoneArea.Takeover}>
        {isDragging || currentIntent === TakeoverIntent.BROWSE_FILES ? (
          <AssetListEmptyState isDragging />
        ) : (
          <TakeoverBodyContent />
        )}
      </DropZone>
    );
  }

  return <TakeoverEmpty />;
};
